import {
    MouseEvent,
    useState,
    useEffect,
    CSSProperties,
    useContext,
} from "react";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { useHistory } from "react-router";
import { CurrentBrand, OrganizationStatus } from "modules/Types";
import ProfileIcon from "assets/images/profileIcon.svg";
import CustomAvatar from "./CustomAvatar";
import SearchSelector from "./AdminSearchSelectorModal";
import { Search } from "@material-ui/icons";
import Sty, { Container, StyledLabelSpan } from "modules/Sty";
import useBrand, { checkTier } from 'Hooks/useBrand';
import useUser from 'Hooks/useUser';
import useScreen from 'Hooks/useScreen';
import { clsx, deepCopy, makeKey } from "modules/Utils";
import ButtonSymphony from "elements/ButtonSymphony";
import { MAX_BRANDS_BEFORE_TEAM_PLAN_UPGRADE_REQUIRED, PlanType, USER_PERMISSION_ON_BRAND_INFO, UserPermissionOnBrand } from "modules/Const";
import { BrandTierType } from "elements/BrandTier";
import BrandTier from "elements/BrandTier";
import Colors from "modules/Colors";
import Icon from "elements/Icon";
import useOrganizationV2 from "Hooks/useOrganizationV2";
import useAppView from "Hooks/useAppView";
import AdminId from "elements/AdminId";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";

const sortAlphabetically = (brands: CurrentBrand[]) => {
    const sortedBrands = deepCopy(brands);
    return (
        sortedBrands.sort(
            (a: CurrentBrand, b: CurrentBrand) =>
                a.name
                    ?.toLowerCase()
                    .localeCompare(b.name?.toLowerCase() as string) as number
        )
    );
}

interface BrandDropdownProps {
    className: string;
    smallSidebar?: boolean;
    onClickBrand?: () => void; // For mobile compatibility
}

const BrandDropdown = ({
    className,
    onClickBrand,
}: BrandDropdownProps) => {
    const history = useHistory();
    const { isSymphonyAdmin } = useUser()
    const { mobileView, mStyle } = useScreen();
    const {
        isProBrand,
        currentBrand,
        loadedBrands,
        setCurrentBrand,
        getBrand,
        getBrandImage,
        resetBrandData,
        hasViewOnlyTeamBrands
    } = useBrand();
    const { setAppView } = useAppView();

    const {
        organization,
        organizationBrandIds,
        hasTeamViewAccess,
        openAddBrandModal,
        // brandIdIsInOrganization
        // } = useOrganization()
    } = useOrganizationV2()
    const { isTeamView } = useAppView();
    const { handleOpenModal } = useContext(UpgradeModalContext);

    // Individual brand account logic - only count brands where user is the owner
    const ownedBrandsCount = loadedBrands?.filter(brand =>
        brand.permissionLevel === UserPermissionOnBrand.OWNER || brand.permissionLevel === UserPermissionOnBrand.ADMINISTRATOR
    ).length || 0;

    // Count team brands (brands from any organization)
    const teamBrandsCount = loadedBrands?.filter(brand => brand.organizationId).length || 0;

    // Check if user has view-only team brands - if so, restrict brand addition
    const hasViewOnlyRestriction = hasViewOnlyTeamBrands()

    /**
     * Smart render function for bottom dropdown text
     * Logic:
     * - If user has view-only team brands: don't show button (handled by hasViewOnlyRestriction)
     * - If user has team brands: show "+ Add a Brand" (they can add through team)
     * - If user has < 2 owned brands and no team: show "+ Create a Brand (2 max)"
     * - If user has >= 2 owned brands and no team: show "Create a Team"
     */
    const renderBottomTextForDropdown = () => {
        // If user has team brands, they can add more through team interface
        if (teamBrandsCount > 0) {
            return '+ Add a Brand';
        }

        // If user has < 2 owned brands, they can add more individual brands
        if (ownedBrandsCount < MAX_BRANDS_BEFORE_TEAM_PLAN_UPGRADE_REQUIRED) {
            return '+ Create a Brand (2 max)';
        }

        // If user has >= 2 owned brands, they need to create a team
        return 'Create a Team';
    };

    const opacity = currentBrand || organization ? 1 : 0;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openAdminSelector, setOpenAdminSelector] = useState<boolean>(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

    // Handle body scroll lock for mobile menu
    useEffect(() => {
        if (mobileView && isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [mobileView, isMobileMenuOpen]);

    const handleOpenDropdown = (event: MouseEvent<HTMLButtonElement>) => {
        if (mobileView) {
            setIsMobileMenuOpen(true);
        } else {
            setAnchorEl(event.currentTarget);
        }
    }

    const handleCloseDropdown = () => {
        if (mobileView) {
            setIsMobileMenuOpen(false);
        } else {
            setAnchorEl(null);
        }
    }


    const handleClickBrandFromDropdown = async (brand: CurrentBrand) => {
        // NOTE: this executes when you click on a brand (artist) in the dropdown
        handleCloseDropdown();

        // Call the mobile callback if provided
        if (onClickBrand) {
            onClickBrand();
        }

        try {
            // Check if we're switching to the same brand (avoid unnecessary work)
            if (brand.id === currentBrand?.id) {
                return;
            }

            // Explicitly switch to brand view first to avoid navbar flickering
            setAppView('brand', history);

            const updatedBrandData = await getBrand(brand.id)
            setCurrentBrand(updatedBrandData); // This handles resetBrandData internally
        } catch (error) {
            console.error('Error updating brand:', error);
        }
    };

    const handleClickTeamHome = () => {
        // Switch to team home view
        handleCloseDropdown();

        // Call the mobile callback if provided
        if (onClickBrand) {
            onClickBrand();
        }

        // Explicitly switch to team view
        setAppView('team', history);

        // Clear brand-specific data but preserve organization data for Team Home
        if (currentBrand) {
            resetBrandData(true); // Preserve organization data for Team Home
            setCurrentBrand(undefined);
        }
    }

    const renderBrandDropdownTitle = () => {
        // In team view, always show organization name regardless of currentBrand
        if (isTeamView() && organization) {
            return organization.name;
        }

        // In brand view, show current brand if available
        if (currentBrand) {
            return currentBrand.name;
        }

        return '';
    }

    const renderBrandDropdownSubtitle = () => {
        // In team view, always show team dashboard subtitle regardless of currentBrand
        if (organization && isTeamView()) {
            return (
                <div style={{ ...Sty.text12, color: Colors.greyNavbar }}>
                    Team Dashboard
                </div>
            );
        }

        // In brand view, show brand tier and ID if available
        if (currentBrand) {
            return (
                <>
                    <BrandTier
                        className="brandTierProfileDropdown"
                        tier={currentBrand?.tier as PlanType}
                        type={BrandTierType.TEXT}
                    />
                    <AdminId
                        className="adminIdProfileDropdown2"
                        id={currentBrand?.id}
                    />
                </>
            );
        }

        return null;
    }

    const renderBrandDropdownAvatar = () => {
        const avatarProps = {
            className: "avatarUserStatusProfileDropdown",
            ...(mobileView && {
                style: {
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                }
            })
        };

        // In team view, always show organization avatar regardless of currentBrand
        if (isTeamView() && organization) {
            return (
                <CustomAvatar
                    {...avatarProps}
                    image={organization.profile_picture}
                    isTeamAvatar={true}
                />
            );
        }

        // In brand view, show current brand avatar if available
        if (currentBrand) {
            const image = getBrandImage(currentBrand)
            const isBorder = image ? false : true;
            return (
                <CustomAvatar
                    {...avatarProps}
                    image={image}
                    isBorder={isBorder}
                />
            );
        }

        // Fallback to organization or default
        if (organization) {
            return (
                <CustomAvatar
                    {...avatarProps}
                    image={organization.profile_picture}
                    isTeamAvatar={true}
                />
            );
        }

        // Final fallback to ProfileIcon
        return (
            <CustomAvatar
                {...avatarProps}
                image={ProfileIcon}
                isBorder={false}
            />
        );
    }

    const renderOrganizationListItem = (isMobile = false) => {
        if (!organization) {
            return null;
        }

        const itemClass = isMobile ? "mobileHeaderMenuItem" : "menuItemProfileDropdown";
        const avatarClass = isMobile ? "fromMobileHeaderCustomAvatar" : "fromBrandDropdownCustomAvatar";
        const itemStyle = isMobile ? sty.mobileHeaderMenuItem : sty.menuItemProfileDropdown;

        return (
            <div
                key="team-home"
                className={itemClass}
                style={{
                    ...itemStyle,
                    backgroundColor: !currentBrand ? 'rgba(0,0,0,0.1)' : 'transparent'
                }}
                onClick={handleClickTeamHome}
            >
                <CustomAvatar
                    className={avatarClass}
                    image={organization.profile_picture}
                    isTeamAvatar={true}
                    {...(isMobile && { isDiv: true })}
                />
                <div
                    className="brandDropdownMenuItemsContent"
                    style={isMobile ? { flex: 1, display: 'flex', flexDirection: 'column' } : undefined}
                >
                    <div
                        className="brandNameProfileDropdown"
                        style={sty.brandNameProfileDropdown}
                    >
                        Team Home
                    </div>
                    <div
                        className="organizationNameProfileDropdown"
                        style={isMobile ? {
                            fontSize: 12,
                            color: Colors.goldDark,
                            fontWeight: 400,
                        } : {
                            ...Sty.text12,
                            color: Colors.goldDark,
                        }}
                    >
                        {organization.name}
                    </div>
                </div>
                {!currentBrand &&
                    <Icon
                        className="selectedIndicator"
                        icon="check"
                        color={Colors.green}
                        size={16}
                        style={{ marginLeft: 'auto' }}
                    />
                }
            </div>
        );
    };

    const renderBrandItem = (brand: CurrentBrand, index: number, isMobile = false) => {
        const { name, image, permissionLevel } = brand;
        const tier = isMobile ? brand.tier : checkTier(brand);

        if (isMobile) {
            return (
                <div
                    key={makeKey(name, index)}
                    className="mobileHeaderMenuItem"
                    style={sty.mobileHeaderMenuItem}
                    onClick={() => handleClickBrandFromDropdown(brand)}
                >
                    <CustomAvatar
                        className="fromMobileHeaderCustomAvatar"
                        image={image ? image : ProfileIcon}
                        isDiv
                    />
                    <div className="brandNameMobileHeader">
                        {name}
                    </div>
                    <BrandTier
                        className="brandTierMobileHeader"
                        style={{ marginLeft: 'auto' }}
                        tier={tier}
                        type={BrandTierType.TEXT}
                        iconSize={12}
                        textSize={14}
                        isWhiteBackground={false}
                    />
                </div>
            );
        }

        // Desktop version
        const isBrandInOrganization = organizationBrandIds?.includes(brand.id);
        const brandOrganization = brand.organization || organization;

        return (
            <div
                key={makeKey(name, index)}
                className="menuItemProfileDropdown"
                style={sty.menuItemProfileDropdown}
                onClick={() => handleClickBrandFromDropdown(brand)}
            >
                <CustomAvatar
                    className="fromBrandDropdownCustomAvatar"
                    image={image}
                    isBorder={image ? false : true}
                    brandOverride={brand}
                />
                <div className="brandDropdownMenuItemsContent">
                    <div
                        className="brandNameProfileDropdown"
                        style={sty.brandNameProfileDropdown}
                    >
                        {name}
                    </div>
                    {/* Show organization name if brand is in organization and org is ready */}
                    {brandOrganization && brandOrganization.setup_status === OrganizationStatus.READY && isBrandInOrganization &&
                        <div
                            className="organizationNameProfileDropdown"
                            style={{
                                ...Sty.text12,
                                color: Colors.goldDark,
                            }}
                        >
                            {brandOrganization?.name}
                        </div>
                    }
                    {/* Show permission level for any brand in organization, regardless of team access */}
                    {isBrandInOrganization && permissionLevel && permissionLevel !== 'none' &&
                        <div
                            className="permissionLevelProfileDropdown"
                            style={{
                                ...Sty.text12,
                                color: USER_PERMISSION_ON_BRAND_INFO[permissionLevel]?.color || Colors.greyDark,
                            }}
                        >
                            {USER_PERMISSION_ON_BRAND_INFO[permissionLevel]?.title || 'No Permission'}
                        </div>
                    }
                    <AdminId
                        className="adminIdProfileDropdown1"
                        id={brand.id}
                    />
                </div>
                <BrandTier
                    className="brandTierProfileDropdown1"
                    style={{ marginLeft: 'auto' }}
                    tier={tier}
                    type={BrandTierType.TEXT}
                    iconSize={14}
                    textSize={12}
                    isWhiteBackground
                />
            </div>
        );
    };

    const renderBrandsList = (isMobile = false) => {
        if (!loadedBrands || loadedBrands.length === 0) {
            if (!isMobile && !organization) {
                return null;
            }
            return isMobile ? null : <div className="brandDropdownMenuItems" style={{ minWidth: 280 }}>
                {renderAdminSearchItem()}
                {hasTeamViewAccess() && renderOrganizationListItem()}
            </div>;
        }

        const sortedBrands = sortAlphabetically(loadedBrands).map((brand: CurrentBrand, index: number) => 
            renderBrandItem(brand, index, isMobile)
        );

        if (isMobile) {
            return sortedBrands;
        }

        return (
            <div className="brandDropdownMenuItems" style={{ minWidth: 280 }}>
                {renderAdminSearchItem()}
                {hasTeamViewAccess() && renderOrganizationListItem()}
                {sortedBrands}
            </div>
        );
    };

    const renderAdminSearchItem = () => {
        if (!isSymphonyAdmin) return null;

        return (
            <MenuItem
                key="admin-search"
                className="adminSearchBrandProfile"
                style={sty.menuItemProfileDropdown}
                onClick={() => {
                    setOpenAdminSelector(true)
                    handleCloseDropdown()
                }}
            >
                <Container>
                    <Search />
                </Container>
                <Container display="flex" flexDirection="column" alignItems="flex-start" width="100%">
                    <StyledLabelSpan
                        padding="0px 0px 0px 16px"
                        fontSize={16}
                        fontWeight={400}
                    >
                        Open Admin Search
                    </StyledLabelSpan>
                    {isSymphonyAdmin &&
                        <StyledLabelSpan
                            paddingLeft="16px"
                            color="#E08507"
                            fontSize={12}
                            fontWeight={400}
                        >
                            Search for any artist as an admin.
                        </StyledLabelSpan>
                    }
                </Container>
            </MenuItem>
        );
    }

    const renderAddNewBrandButton = (isMobile = false) => {
        const handleAddBrand = () => {
            if (organization && hasTeamViewAccess()) {
                openAddBrandModal();
                handleCloseDropdown();
                return;
            }

            // Check if user needs team plan (>= 2 owned brands and no team)
            if (ownedBrandsCount >= MAX_BRANDS_BEFORE_TEAM_PLAN_UPGRADE_REQUIRED && teamBrandsCount === 0) {
                handleOpenModal({
                    source: 'brand_dropdown_add_brand',
                    tab: 'team',
                    upgradeCallToAction: 'Manage your roster & add more than 2 brands with the Team Plan',
                    highlightedPlan: PlanType.TEAM_V2,
                    onlyShowPlans: [PlanType.TEAM_V2]
                });
            } else {
                history.push('/add-artist');
            }

            if (isMobile) {
                handleCloseDropdown();
            }
        };

        if (isMobile) {
            return (
                <div
                    key="add artist"
                    className="mobileHeaderMenuItemBottom"
                    style={{...sty.mobileHeaderMenuItem, height: 57}}
                    onClick={handleAddBrand}
                >
                    <ButtonSymphony
                        className="mobileHeaderMenuItemBottomButton"
                        variant="text"
                    >
                        + Add New Brand
                    </ButtonSymphony>
                </div>
            );
        }

        // Desktop version
        return (
            <ButtonSymphony
                key="add-new-brand"
                className="mainAddNewBrandButton"
                style={sty.mainAddNewBrandButton}
                onClick={handleAddBrand}
                variant="text"
            >
                {renderBottomTextForDropdown()}
            </ButtonSymphony>
        );
    };

    const renderBrandDropdownTextContainer = () => {
        if (mobileView) {
            return (
                <div className="brandNameProContainer">
                    <div className="brandName" style={{ color: 'white' }}>
                        {renderBrandDropdownTitle()}
                    </div>

                    {isProBrand &&
                        <div
                            className="proAccountContainer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            <Icon
                                className="proAccountIcon2"
                                color={Colors.gold}
                                icon="gem"
                                size={12}
                            />
                            <div
                                className="proAccountText"
                                style={{ color: Colors.gold, fontSize: 10 }}
                            >
                                PRO ACCOUNT
                            </div>
                        </div>
                    }
                </div>
            );
        }

        // Desktop structure
        return (
            <div style={sty.brandDropdownTextContainer}>
                <div
                    className="brandDropdownTitle"
                    style={{
                        ...Sty.text16white,
                        ...sty.brandDropdownTitle,
                    }}
                >
                    {renderBrandDropdownTitle()}
                </div>
                <div className="brandDropdownTierContainer" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {renderBrandDropdownSubtitle()}
                </div>
            </div>
        );
    };

    const renderMobileMenu = () => {
        if (!isMobileMenuOpen) {
            return null;
        }

        return (
            <div
                className="mobileHeaderMenuOverlay"
                style={sty.mobileHeaderMenuOverlay}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsMobileMenuOpen(false);
                }}
            >
                <div
                    className="mobileHeaderMenu"
                    style={sty.mobileHeaderMenu}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Hide Add New Brand button for users with view-only team brands */}
                    {!hasViewOnlyRestriction && renderAddNewBrandButton(true)}
                    {hasTeamViewAccess() && renderOrganizationListItem(true)}
                    {renderBrandsList(true)}
                </div>
            </div>
        )
    }

    // NOTE: render()
    return (
        <>
            <style>
                {`
                    .mainAddNewBrandButton, .menuItemProfileDropdown {
                        &:hover {
                        background-color: rgba(0,0,0,0.05) !important;
                        }
                    }
                `}
            </style>

            {/* NOTE: this is the Dropdown Button */}
            <div
                className={clsx('brandDropdownComponent', className)}
                style={mStyle(sty, 'brandDropdownComponent')}
            >
                <ButtonSymphony
                    className="brandDropdownButton"
                    style={mStyle(sty, 'brandDropdownButton')}
                    styleButtonContent={{
                        width: '100%',
                    }}
                    onClick={handleOpenDropdown} // NOTE: this opens brandDropdownMenu
                >
                    {renderBrandDropdownAvatar()}
                    {renderBrandDropdownTextContainer()}
                    <Icon
                        className="brandDropdownIcon"
                        style={mStyle(sty, 'brandDropdownButtonIcon')}
                        icon="chevronDown"
                        size={16}
                    />
                </ButtonSymphony>

                {/* NOTE: Desktop Menu or Mobile Overlay */}
                {mobileView
                ? renderMobileMenu()
                : (
                    // Desktop Menu
                    <Menu
                        id="simple-menu"
                        className="brandDropdownMenu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleCloseDropdown}
                        MenuListProps={{
                            style: {
                                padding: 0,
                                maxWidth: 400
                            },
                        }}
                    >
                        {(loadedBrands || organization) &&
                            renderBrandsList(false)
                        }
                        {/* Smart button rendering based on user's brand situation */}
                        {!hasViewOnlyRestriction && renderBottomTextForDropdown() && 
                            renderAddNewBrandButton(false)
                        }
                    </Menu>
                )}
                {isSymphonyAdmin &&
                    <SearchSelector
                        className="fromBrandDropdownSearchSelector"
                        mode="default"
                        open={openAdminSelector}
                        onClose={() => {
                            setOpenAdminSelector(false)
                            handleCloseDropdown()
                        }}
                    />
                }
            </div>
        </>
    );
};

const sty: Record<string, CSSProperties> = {
    mainAddNewBrandButton: {
        display: 'flex',
        width: '100%',
        paddingTop: 18,
        paddingBottom: 16,
        borderRadius: 0,
        gap: 8,
    },
    brandDropdownButtonIcon: {
        opacity: 0.5,
        marginLeft: 'auto',
        color: Colors.white,
    },
    brandDropdownButtonIcon_mobile: {
        opacity: 'none',
        color: Colors.greyNavbar
    },
    brandDropdownButton: {
        backgroundColor: 'transparent',
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 6,
        paddingBottom: 6,
        height: 'auto',
        textAlign: 'left',
        justifyContent: 'flex-start',
        width: '100%',
    },
    brandDropdownButton_mobile: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderRadius: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        padding: 8,
        gap: 8,
        height: 'auto',
        textAlign: 'left' as const,
        justifyContent: 'flex-start',
    },
    brandDropdownTextContainer: {
        flex: 1,
        minWidth: 0, // Allows flex item to shrink below content size
    },
    brandDropdownComponent: {
        cursor: 'pointer',
        opacity: 'opacity',
        borderRadius: 16,
        paddingTop: 4,
        paddingBottom: 4,
    },
    brandDropdownComponent_mobile: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 0,
        width: '100%',
        height: 88,
        backgroundColor: Colors.purpleDark,
        flexShrink: 0,
        paddingLeft: 16,
        paddingRight: 16,
        position: 'relative' as const,
    },
    brandDropdownTitle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    brandDropdownUserNameText: {
        color: "white",
        fontSize: 16,
        textAlign: "left",
        padding: "0 4px 0 4px",
        width: "120px",
        height: "56px"
    },
    menuItemProfileDropdown: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: 8,
        cursor: 'pointer',
        minHeight: 67,
        borderBottom: Sty.border,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 16,
        paddingRight: 16,
    },
    brandNameProfileDropdown: {
        fontSize: 16,
        fontWeight: 400,
        width: '100%',
        color: Colors.greyDark,
    },
    // Mobile-specific styles
    mobileHeaderMenuOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        zIndex: 1000,
    },
    mobileHeaderMenu: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 275,
        backgroundColor: 'white',
        width: '100%',
        zIndex: 1000,
        overflowY: 'auto',
        boxShadow: '0 8px 12px -2px rgba(0, 0, 0, 0.2), 0 4px 8px -2px rgba(0, 0, 0, 0.2)'
    },
    mobileHeaderMenuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 16,
        paddingRight: 16,
        borderBottom: Sty.border,
        cursor: 'pointer'
    },
}

export default BrandDropdown;
